import React, { useEffect, useId, useRef, useState } from 'react';
import { Button } from 'antd';
import { useTranslation } from 'react-i18next';
import BrandLoader from '../Brand/BrandLoader';
import { CameraSwitchIcon, MicrophoneOffIcon, RecordIcon, StopIcon, TrashIcon } from '../Brand/Icon';
import { RecordingSession, RECORDING_LIMITS } from '../../media/recorder';
import { recordingErrorInfo } from '../../media/recordingError';
import { canOpenMediaSettings, openMediaSettings } from '../../media/devicePermissions';
import mediaTranslations from '../../mediaTranslations';
import VoiceVisualizer from './VoiceVisualizer';

export default function MediaRecorderPanel({ kind, onAdd, onCancel, onRecover, enabled, backActionRef }) {
  const { t } = useTranslation();
  const waitingDescription = useId();
  const [phase, setPhase] = useState('permission');
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState(null);
  const [stream, setStream] = useState(null);
  const [cameraCount, setCameraCount] = useState(1);
  const [facingMode, setFacingMode] = useState('user');
  const [silent, setSilent] = useState(false);
  const [settingsError, setSettingsError] = useState(false);
  const [openingSettings, setOpeningSettings] = useState(false);
  const session = useRef(null);
  const video = useRef(null);
  const recovery = useRef(onRecover);
  recovery.current = onRecover;
  const addition = useRef(onAdd);
  addition.current = onAdd;
  const lifecycle = useRef(null);
  const panel = useRef(null);

  useEffect(() => {
    let mounted = true;
    // A late completion belongs to its own session, even after the recording
    // kind changes or React recreates effects in development StrictMode.
    const take = { active: true, handled: false, complete: null };
    lifecycle.current = take;
    setElapsed(0);
    setError(null);
    setCameraCount(1);
    setFacingMode('user');
    setSilent(false);
    setSettingsError(false);
    setOpeningSettings(false);
    const controller = new RecordingSession({ kind,
      onState: (next, duration) => { if (mounted) { setPhase(next); if (duration !== undefined) setElapsed(duration); } },
      onStream: value => { if (mounted) setStream(value); },
      onError: value => { if (mounted) setError(value); },
      onComplete: value => {
        if (take.handled) return;
        take.complete = value;
        if (mounted) {
          take.handled = true;
          addition.current(value);
        } else { take.handled = true; recovery.current?.(value); }
      },
    });
    session.current = controller;
    controller.prepare();
    panel.current?.focus({ preventScroll: true });
    return () => {
      mounted = false;
      take.active = false;
      controller.dispose({ discard: take.handled });
      if (take.complete && !take.handled) {
        take.handled = true;
        recovery.current?.(take.complete);
      }
    };
  }, [kind]);

  useEffect(() => {
    if (video.current) video.current.srcObject = stream;
    if (stream && kind === 'video') {
      let active = true;
      navigator.mediaDevices.enumerateDevices?.().then(devices => {
        if (active) setCameraCount(devices.filter(device => device.kind === 'videoinput').length);
      }).catch(() => {});
      return () => { active = false; };
    }
    return undefined;
  }, [stream, kind]);

  // Finish a live take before capabilities rebuild the toolbar. Its data stays
  // in the editor even when new server uploads have become unavailable.
  useEffect(() => {
    if (!enabled && ['permission', 'prepared', 'recording'].includes(phase)) session.current?.stop();
  }, [enabled, phase]);
  const prepare = (nextFacing = facingMode, nextSilent = silent) => {
    if (lifecycle.current) lifecycle.current.complete = null;
    setElapsed(0);
    setError(null);
    setSettingsError(false);
    session.current?.prepare({ facingMode: nextFacing, silent: nextSilent });
  };
  const cancel = () => {
    if (lifecycle.current) lifecycle.current.handled = true;
    session.current?.dispose({ discard: true });
    onCancel();
  };
  backActionRef.current = () => {
    if (phase === 'recording') session.current?.stop();
    else if (['permission', 'prepared', 'error', 'interrupted'].includes(phase)) cancel();
    else panel.current?.focus({ preventScroll: true });
  };
  const seconds = Math.floor(elapsed / 1000);
  const maxSeconds = RECORDING_LIMITS[kind].seconds;
  const time = value => `${Math.floor(value / 60)}:${String(value % 60).padStart(2, '0')}`;
  const audio = kind === 'audio';
  const retry = phase === 'error' || phase === 'interrupted';
  const stopping = phase === 'recording' || phase === 'stopping';
  const primaryLabel = t(stopping ? 'media:stop' : retry ? 'media:recordAgain' : 'media:start',
    { defaultValue: stopping ? 'Stop recording' : retry ? 'Record again' : 'Start recording' });
  const awaitingCapture = phase === 'permission' || phase === 'stopping';
  const waitingLabel = t(phase === 'permission' ? 'media:permissionWaiting' : 'media:finishing',
    { defaultValue: phase === 'permission' ? 'Waiting for permission…' : 'Finishing recording…' });
  const silentRetry = !audio && phase === 'error' && !silent && error?.resource !== 'camera';
  const errorInfo = error ? recordingErrorInfo(error, { kind }) : null;
  const showSettings = errorInfo?.name === 'NotAllowedError' && error?.permission !== 'restricted' && canOpenMediaSettings();
  const goToSettings = async () => {
    if (openingSettings) return;
    const current = lifecycle.current;
    const captureGeneration = session.current?.generation;
    setOpeningSettings(true);
    setSettingsError(false);
    try { await openMediaSettings(); }
    catch {
      if (lifecycle.current === current && current.active && !current.handled
          && session.current?.generation === captureGeneration) setSettingsError(true);
    }
    finally { if (lifecycle.current === current && current.active && !current.handled) setOpeningSettings(false); }
  };

  return <section className={`editor-recorder editor-recorder--${kind}`} tabIndex={-1}
    data-phase={phase} aria-busy={awaitingCapture} aria-describedby={awaitingCapture ? waitingDescription : undefined}
    ref={panel} aria-label={t(kind === 'audio' ? 'media:recordAudio' : 'media:recordVideo',
      { defaultValue: kind === 'audio' ? 'Record audio' : 'Record video' })} data-no-swipe="true">
    {awaitingCapture && <span id={waitingDescription} hidden>{waitingLabel}</span>}
    {audio && <div className="editor-recorder__meter">
      {phase === 'permission' || phase === 'stopping'
        ? <BrandLoader compact label={t(phase === 'permission' ? 'media:permissionWaiting' : 'media:finishing',
          { defaultValue: phase === 'permission' ? 'Waiting for permission…' : 'Finishing recording…' })} />
        : <VoiceVisualizer stream={stream} active={phase === 'recording'} />}
      <p className="editor-recorder__timer" role="timer">{time(seconds)} / {time(maxSeconds)}</p>
    </div>}
    {!audio && stream && <div className="editor-recorder__preview"><div className="editor-recorder__viewfinder">
      <video ref={video} className="editor-recorder__camera" autoPlay muted playsInline />
      {awaitingCapture && <BrandLoader compact label={waitingLabel} />}
    </div></div>}
    {!audio && !stream && awaitingCapture && <BrandLoader compact label={waitingLabel} />}
    {phase === 'interrupted' && !errorInfo && <p role="status">
      {t('media:recordingInterrupted', { defaultValue: mediaTranslations.en.recordingInterrupted })}
    </p>}
    {!audio && <p className="editor-recorder__timer" role="timer">{time(seconds)} / {time(maxSeconds)}</p>}
    {errorInfo && <p role="alert" data-error-name={errorInfo.name}>
      {t(`media:${errorInfo.key}`, { defaultValue: mediaTranslations.en[errorInfo.key] })}
    </p>}
    {showSettings && <Button className="editor-recorder__settings" disabled={openingSettings} onClick={goToSettings}>
      {t('media:openSettings', { defaultValue: mediaTranslations.en.openSettings })}
    </Button>}
    {settingsError && <p role="alert">
      {t('media:recordingSettingsError', { defaultValue: mediaTranslations.en.recordingSettingsError })}
    </p>}
    {!enabled && <p role="status">{t('media:recordingDisabled', { defaultValue: 'New recordings are temporarily unavailable. Your recording stays on this device.' })}</p>}
    <div className={`editor-recorder__actions editor-recorder__actions--${kind}`}>
      <Button icon={<TrashIcon />} aria-label={t('media:discardRecording', { defaultValue: 'Discard recording' })}
        onClick={cancel} />
      <Button icon={stopping ? <StopIcon /> : <RecordIcon />} className="editor-recorder__record"
        aria-label={primaryLabel} disabled={phase === 'permission' || phase === 'stopping' || (!enabled && !stopping)}
        onClick={() => stopping ? session.current.stop() : retry ? prepare() : session.current.start()} />
      {!audio && <Button icon={silentRetry ? <MicrophoneOffIcon /> : <CameraSwitchIcon />}
        aria-label={t(silentRetry ? 'media:withoutSound' : 'media:switchCamera',
          { defaultValue: silentRetry ? 'Try video without sound' : 'Switch camera' })}
        disabled={!enabled || (!silentRetry && (phase !== 'prepared' || cameraCount < 2))}
        onClick={() => {
          if (silentRetry) { setSilent(true); prepare(facingMode, true); }
          else {
            const next = facingMode === 'user' ? 'environment' : 'user';
            setFacingMode(next); prepare(next);
          }
        }} />}
    </div>
  </section>;
}
